import { Box, Button, Link, Stack } from '@mui/material';
import { ExecutionResult } from 'graphql';
import { t } from 'i18next';
import { Suspense, lazy } from 'react';
import { getOptionalAuthHeader, isAqueductEnabled } from '../../auth/utils';
import { PageLoader } from '../../common/components/PageLoader';
import { BaseController } from '../../common/controllers/BaseController';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { bind } from '../../common/utils/decorators';
import { FeatureFlagsType, getFeatureFlag } from '../../config/launch-darkly/FeatureFlagsController';
import { DocumentTitle } from '../../layout/components/DocumentTitle';
import { ModalManager } from '../../modal-flows/services/ModalManager';
import { EXTERNAL_URLS } from '../../routing/routes';

type Props = {};

type State = {};

type FetcherParams = {
  query: string;
  operationName?: string | null;
  variables?: Record<string, string>;
};

const DEFAULT_QUERY = `
# In this box you can write GraphQL queries to execute against live production data.
# The following example fetches the username and the names of the projects available to the user.

{
  viewer {
    name
    organization {
      projects {
        edges {
          node {
            name
          }
        }
      }
    }
  }
}

# Click the "DOCS" icon in the top left corner to explore the available types.
# Click the "API Documentation" link in the bottom right corner to learn how to use the AQtive Guard API.
`;

type LazyExplorerProps = {
  fetcher: (params: FetcherParams) => Promise<ExecutionResult>;
  onResetClick: () => void;
};

const LazyLoadedExplorer = lazy(async () => {
  const [{ GraphiQL }] = await Promise.all([import('graphiql'), import('graphiql/graphiql.min.css')]);

  return {
    default: ({ fetcher, onResetClick }: LazyExplorerProps) => {
      const enableIntrospection = getFeatureFlag(FeatureFlagsType.ENABLE_GRAPHQL_INTROSPECTION);
      const useAqueduct = isAqueductEnabled();

      return (
        <Box className="api-explorer" sx={{ width: 1, height: 1, '& .graphiql-container .graphiql-logo': { p: 0.5 }, '& .graphiql-tab-add': { p: 1 } }}>
          <GraphiQL fetcher={fetcher} defaultQuery={DEFAULT_QUERY} schema={enableIntrospection ? undefined : null}>
            <GraphiQL.Logo>
              <Box sx={{ display: 'none' }} />
            </GraphiQL.Logo>
            <GraphiQL.Footer>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'flex-end', pt: 1.5, pr: 0.75, pb: 0.75 }}>
                <Link variant="button" color="primary" target="_blank" href={UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_API)}>
                  {t('apiDocs')}
                </Link>
                {!useAqueduct && (
                  <Button variant="contained" color="error" onClick={onResetClick}>
                    {t('resetApiKey.cta')}
                  </Button>
                )}
              </Stack>
            </GraphiQL.Footer>
          </GraphiQL>
        </Box>
      );
    },
  };
});

export class ApiExplorerController extends BaseController<Props, State> {
  private readonly modalManager = new ModalManager();

  render() {
    return this.renderBase(
      <Suspense fallback={<PageLoader />}>
        <DocumentTitle loading={false} fragments={[t('apiExplorer')]} />
        <LazyLoadedExplorer fetcher={this.fetcher} onResetClick={this.handleResetKey} />
      </Suspense>
    );
  }

  @bind
  private async fetcher(params: FetcherParams) {
    const response = await fetch('/api/v2', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getOptionalAuthHeader() },
      body: JSON.stringify(params),
    });
    try {
      return (await response.json()) as ExecutionResult;
    } catch {
      return (await response.text()) as ExecutionResult;
    }
  }

  @bind
  private async handleResetKey() {
    // Storage of the new key is delegated to the modal to avoid missing
    // it in case the user navigates away from the page without closing it
    await this.modalManager.resetApiKey();
  }
}
